import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { adminApi } from '../api';
import { brandingApi } from '../apiExtras';
import { Plus, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';

const FALLBACK_CATEGORIES = ['Wedding','Ritratti','Aziende','Diciottesimi','Famiglia','Eventi','Battesimo','Comunione','Altro'];

const CreateGalleryDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [form, setForm] = useState({ title: '', category: FALLBACK_CATEGORIES[0], date: new Date().toLocaleDateString('it-IT'), firstSubTitle: 'Foto' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const cats = await brandingApi.categories();
        if (cats && cats.length) {
          setCategories(cats);
          setForm(f => ({ ...f, category: cats.includes(f.category) ? f.category : cats[0] }));
        }
      } catch (err) {
        console.warn('CreateGalleryDialog: could not fetch categories, using fallback', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Titolo obbligatorio');
    if (!form.firstSubTitle.trim()) return toast.error('La prima sotto-galleria richiede un titolo');
    setBusy(true);
    try {
      const gallery = await adminApi.createGallery(clientId, {
        title: form.title.trim(), category: form.category, date: form.date,
      });
      // Create first sub-gallery so the constraint "gallery must have >=1 subgallery" is respected
      await adminApi.createSubgallery(gallery.id, { title: form.firstSubTitle.trim() });
      toast.success('Lavoro creato. Ora puoi aggiungere le foto nella sotto-galleria.');
      onCreated && await onCreated(gallery);
      onOpenChange(false);
      setForm({ title:'', category: categories[0] || 'Altro', date:new Date().toLocaleDateString('it-IT'), firstSubTitle: 'Foto' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore creazione'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v && !busy) setForm({ title:'', category: categories[0] || 'Altro', date:new Date().toLocaleDateString('it-IT'), firstSubTitle: 'Foto' }); onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nuovo lavoro</DialogTitle>
          <DialogDescription>Crea il contenitore del lavoro. Dopo aggiungerai le sotto-gallerie (cartelle di foto).</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid md:grid-cols-3 gap-3">
            <label className="block md:col-span-2">
              <span className="text-sm font-medium text-slate-700">Titolo del lavoro</span>
              <input required value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="Es. Matrimonio, Book Fotografico, Diciottesimo" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#F7F1E1]" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Data</span>
              <input value={form.date} onChange={e=>setForm({...form, date:e.target.value})} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#F7F1E1]" />
            </label>
            <label className="block md:col-span-3">
              <span className="text-sm font-medium text-slate-700">Categoria</span>
              <select value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#F7F1E1] bg-white">
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
          </div>

          <div className="bg-[#F7F1E1]/40 border border-[#C9A961]/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#8A7434] mb-2">
              <FolderPlus className="w-4 h-4" /> Prima sotto-galleria
            </div>
            <p className="text-xs text-slate-600 mb-2">Ogni lavoro contiene almeno una sotto-galleria. Puoi rinominarla e aggiungerne altre dopo.</p>
            <input value={form.firstSubTitle} onChange={e=>setForm({...form, firstSubTitle:e.target.value})} placeholder="Es. Foto, Preparativi, Cerimonia" className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A961] bg-white" />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Annulla</button>
            <button disabled={busy} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors disabled:opacity-70">
              <Plus className="w-4 h-4" /> {busy ? 'Creazione...' : 'Crea lavoro'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGalleryDialog;
